import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { forkJoin } from 'rxjs';
import { DataService } from '../../../interfaces/DataService';
import {
  SchemaPermissionLevel,
  SchemaPermissionNode,
  UserSchemaPermission
} from '../../../interfaces/SchemaPermission';
import { BeNeo4jService } from '../../../services/be-neo4j.service';
import { SharedService } from '../../../services/shared.service';
import { UserDialogData } from '../add-user-company-dialog/add-user-company-dialog.component';

interface SchemaPermissionRow extends SchemaPermissionNode {
  permission: SchemaPermissionLevel | null;
  initialPermission: SchemaPermissionLevel | null;
}

@Component({
  selector: 'app-add-schema-permissions-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTableModule
  ],
  templateUrl: './add-schema-permissions-user-dialog.component.html',
  styleUrl: './add-schema-permissions-user-dialog.component.scss'
})
export class AddSchemaPermissionsUserDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AddSchemaPermissionsUserDialogComponent>, { optional: true });
  readonly data = inject<UserDialogData>(MAT_DIALOG_DATA);

  readonly displayedColumns = ['name', 'hierarchy', 'nodeLabel', 'permission'];
  readonly permissionLevels: SchemaPermissionLevel[] = ['viewer', 'editor', 'manager'];
  readonly dataServices: DataService[];

  selectedConnection = '';
  schemaSearch = '';
  schemas: SchemaPermissionRow[] = [];
  isLoading = false;
  isSaving = false;

  constructor(
    private readonly beNeo4jService: BeNeo4jService,
    private readonly sharedService: SharedService
  ) {
    this.dataServices = this.sharedService.data_services ?? [];
    this.selectedConnection = this.getInitialConnection();

    if (this.selectedConnection) {
      this.loadSchemas();
    }
  }

  get pendingChangesCount(): number {
    return this.schemas.filter(
      schema => schema.permission !== null && schema.permission !== schema.initialPermission
    ).length;
  }

  get filteredSchemas(): SchemaPermissionRow[] {
    const search = this.schemaSearch.trim().toLocaleLowerCase();
    if (!search) return this.schemas;

    return this.schemas.filter(schema =>
      schema.name.toLocaleLowerCase().includes(search)
    );
  }

  loadSchemas(): void {
    if (!this.selectedConnection) {
      this.schemas = [];
      return;
    }

    this.isLoading = true;
    this.schemas = [];
    forkJoin({
      availableSchemas: this.beNeo4jService.getSchemasManagerPermissions(this.selectedConnection),
      currentPermissions: this.beNeo4jService.getUsersSchemaPermissions(
        this.data.uuid,
        this.selectedConnection,
      )
    }).subscribe({
      next: ({ availableSchemas, currentPermissions }) => {
        console.log(availableSchemas);
        this.schemas = this.mergeCurrentPermissions(
          availableSchemas.result,
          currentPermissions.result
        );
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  savePermissions(): void {
    const selectedSchemas = this.schemas.filter(
      (schema): schema is SchemaPermissionRow & { permission: SchemaPermissionLevel } =>
        schema.permission !== null && schema.permission !== schema.initialPermission
    );

    if (!this.selectedConnection || selectedSchemas.length === 0) return;

    this.isSaving = true;
    const requests = selectedSchemas.map(schema =>
      this.beNeo4jService.setSchemasPermissions(
        this.selectedConnection,
        schema.nodeLabel,
        schema.nodeUUID,
        [[this.data.uuid, schema.permission]]
      )
    );

    forkJoin(requests).subscribe({
      next: () => {
        this.isSaving = false;
        this.dialogRef?.close(true);
      },
      error: () => {
        this.isSaving = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef?.close();
  }

  private getInitialConnection(): string {
    const currentConnection = this.sharedService.data_service?.string_connection;
    const currentIsAvailable = this.dataServices.some(
      dataService => dataService.string_connection === currentConnection
    );

    return currentIsAvailable
      ? currentConnection!
      : this.dataServices[0]?.string_connection ?? '';
  }

  private mergeCurrentPermissions(
    availableSchemas: SchemaPermissionNode[],
    currentPermissions: UserSchemaPermission[]
  ): SchemaPermissionRow[] {
    const permissionsByNode = new Map(
      currentPermissions.map(permission => [
        this.getPermissionNodeKey(permission),
        permission.permission
      ])
    );

    return availableSchemas.map(schema => {
      const permission = permissionsByNode.get(this.getPermissionNodeKey(schema)) ?? null;
      return {
        ...schema,
        permission,
        initialPermission: permission
      };
    });
  }

  private getPermissionNodeKey(node: SchemaPermissionNode): string {
    return `${node.nodeLabel.toLocaleUpperCase()}:${node.nodeUUID}`;
  }
}
